import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Card, Eyebrow, PageHead, Empty } from "@/components/admin/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ChevronDown,
  ChevronUp,
  ImagePlus,
  Loader2,
  Megaphone,
  MessageCircleQuestion,
  Pencil,
  Plus,
  Trash2,
  X,
} from "lucide-react";

const blankBroadcast = {
  kind: "broadcast",
  title: "",
  text: "",
  link: "",
  button_label: "",
  banner_url: "",
  duration_sec: 25,
  options: [],
};

const blankQuestion = {
  kind: "question",
  title: "",
  text: "",
  link: "",
  button_label: "",
  banner_url: "",
  duration_sec: 90,
  options: [
    { key: "a", label: "Yes", follow_up_text: "", follow_up_link: "", follow_up_button: "" },
    { key: "b", label: "No", follow_up_text: "", follow_up_link: "", follow_up_button: "" },
  ],
};

const BannerPicker = ({ value, onChange, testid }) => {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = async (file) => {
    if (!file) return;
    setBusy(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/templates/banner", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
    } catch (e) {
      toast.error(formatApiError(e) || "Couldn't upload that image");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={(e) => pick(e.target.files?.[0])}
        data-testid={`${testid}-input`}
      />
      {value ? (
        <div className="relative">
          <img
            src={value}
            alt=""
            className="w-full h-32 object-cover rounded-2xl ring-1 ring-white/10"
            data-testid={`${testid}-preview`}
          />
          <button
            onClick={() => onChange("")}
            className="absolute top-2 right-2 h-8 w-8 grid place-items-center rounded-full bg-black/70 ring-1 ring-white/20 text-white/70 hover:text-white"
            data-testid={`${testid}-remove`}
            aria-label="Remove banner"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <button
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          className="w-full h-24 rounded-2xl border border-dashed border-white/15 hover:border-emerald-400/40 text-white/40 hover:text-emerald-300 text-[11.5px] flex flex-col items-center justify-center gap-1.5 transition-colors"
          data-testid={`${testid}-btn`}
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
          {busy ? "Uploading…" : "Add a banner image"}
        </button>
      )}
    </div>
  );
};

const Editor = ({ draft, setDraft, onSave, onCancel, saving }) => {
  const isQ = draft.kind === "question";
  const setOpt = (i, patch) => {
    const next = [...draft.options];
    next[i] = { ...next[i], ...patch };
    setDraft({ ...draft, options: next });
  };

  return (
    <div className="rounded-[24px] bg-white/[0.03] ring-1 ring-emerald-400/20 p-5 space-y-4" data-testid="template-editor">
      <Input
        value={draft.title}
        onChange={(e) => setDraft({ ...draft, title: e.target.value })}
        placeholder={isQ ? "Name this question (only you see this)" : "Name this announcement"}
        className="bg-neutral-950 border-neutral-800 h-11"
        data-testid="template-title"
      />
      <BannerPicker
        value={draft.banner_url}
        onChange={(url) => setDraft({ ...draft, banner_url: url })}
        testid="template-banner"
      />
      <textarea
        value={draft.text}
        onChange={(e) => setDraft({ ...draft, text: e.target.value })}
        rows={3}
        placeholder={isQ ? "The question students will see" : "The announcement students will see"}
        className="w-full bg-neutral-950 border border-neutral-800 rounded-xl px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500"
        data-testid="template-text"
      />

      {isQ ? (
        <div className="space-y-3">
          <Eyebrow>Answers &amp; what happens next</Eyebrow>
          {draft.options.map((o, i) => (
            <div key={o.key} className="rounded-2xl bg-black/40 ring-1 ring-white/[0.06] p-3 space-y-2">
              <div className="flex items-center gap-2">
                <Input
                  value={o.label}
                  onChange={(e) => setOpt(i, { label: e.target.value })}
                  placeholder={`Answer ${i + 1}`}
                  className="bg-neutral-950 border-neutral-800 h-9 text-[12.5px]"
                  data-testid={`template-option-label-${i}`}
                />
                {draft.options.length > 2 && (
                  <button
                    onClick={() =>
                      setDraft({ ...draft, options: draft.options.filter((_, j) => j !== i) })
                    }
                    className="h-9 w-9 shrink-0 grid place-items-center rounded-full text-white/35 hover:text-red-300 hover:bg-red-500/10"
                    data-testid={`template-option-remove-${i}`}
                    aria-label="Remove answer"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <textarea
                value={o.follow_up_text}
                onChange={(e) => setOpt(i, { follow_up_text: e.target.value })}
                rows={2}
                placeholder="Message they get after picking this…"
                className="w-full bg-neutral-950 border border-neutral-800 rounded-lg px-2.5 py-2 text-[12px] resize-none"
                data-testid={`template-option-followup-${i}`}
              />
              <div className="grid grid-cols-2 gap-2">
                <Input
                  value={o.follow_up_link}
                  onChange={(e) => setOpt(i, { follow_up_link: e.target.value })}
                  placeholder="https://link (optional)"
                  className="bg-neutral-950 border-neutral-800 h-9 text-[12px]"
                  data-testid={`template-option-link-${i}`}
                />
                <Input
                  value={o.follow_up_button}
                  onChange={(e) => setOpt(i, { follow_up_button: e.target.value })}
                  placeholder="Button text e.g. Pay now"
                  className="bg-neutral-950 border-neutral-800 h-9 text-[12px]"
                  data-testid={`template-option-button-${i}`}
                />
              </div>
            </div>
          ))}
          {draft.options.length < 4 && (
            <button
              onClick={() =>
                setDraft({
                  ...draft,
                  options: [
                    ...draft.options,
                    {
                      key: String.fromCharCode(97 + draft.options.length),
                      label: "",
                      follow_up_text: "",
                      follow_up_link: "",
                      follow_up_button: "",
                    },
                  ],
                })
              }
              className="h-10 px-4 rounded-full ring-1 ring-white/10 text-[11px] uppercase tracking-[0.16em] text-white/50 hover:text-white hover:ring-emerald-400/30"
              data-testid="template-option-add"
            >
              <Plus className="w-3.5 h-3.5 inline mr-1.5" /> Add answer
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <Input
            value={draft.link}
            onChange={(e) => setDraft({ ...draft, link: e.target.value })}
            placeholder="https://link (optional)"
            className="bg-neutral-950 border-neutral-800 h-10 text-[12.5px]"
            data-testid="template-link"
          />
          <Input
            value={draft.button_label}
            onChange={(e) => setDraft({ ...draft, button_label: e.target.value })}
            placeholder="Button text e.g. Enrol now"
            className="bg-neutral-950 border-neutral-800 h-10 text-[12.5px]"
            data-testid="template-button-label"
          />
        </div>
      )}

      <div className="flex items-center gap-3">
        <div className="text-[11px] text-white/40">Stays on screen for</div>
        <Input
          type="number"
          min={5}
          max={900}
          value={draft.duration_sec}
          onChange={(e) => setDraft({ ...draft, duration_sec: Number(e.target.value) || 25 })}
          className="bg-neutral-950 border-neutral-800 h-9 w-24 text-[12.5px]"
          data-testid="template-duration"
        />
        <div className="text-[11px] text-white/40">seconds</div>
      </div>

      <div className="flex gap-2 pt-1">
        <Button
          onClick={onSave}
          disabled={saving || !draft.title.trim()}
          className="rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-semibold h-11 px-7"
          data-testid="template-save"
        >
          {saving ? "Saving…" : "Save template"}
        </Button>
        <Button
          onClick={onCancel}
          variant="ghost"
          className="rounded-full h-11 px-6 text-white/50 hover:text-white"
          data-testid="template-cancel"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default function Templates() {
  const [kind, setKind] = useState("broadcast");
  const [items, setItems] = useState(null);
  const [draft, setDraft] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api
      .get("/templates")
      .then(({ data }) => setItems(data?.templates || []))
      .catch(() => setItems([]));
  };
  useEffect(load, []);

  const list = (items || []).filter((t) => t.kind === kind);

  const save = async () => {
    setSaving(true);
    try {
      if (editingId) await api.put(`/templates/${editingId}`, draft);
      else await api.post("/templates", draft);
      toast.success(editingId ? "Template updated" : "Template saved");
      setDraft(null);
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e) || "Couldn't save that");
    } finally {
      setSaving(false);
    }
  };

  const move = async (idx, dir) => {
    const next = [...list];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setItems((prev) => [...next, ...prev.filter((t) => t.kind !== kind)]);
    try {
      await api.post("/templates/reorder", { ids: next.map((t) => t.id) });
    } catch (_) {}
  };

  const remove = async (id) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
    try {
      await api.delete(`/templates/${id}`);
      toast.success("Template deleted");
    } catch (e) {
      toast.error(formatApiError(e));
      load();
    }
  };

  return (
    <div className="space-y-7" data-testid="templates-page">
      <PageHead
        eyebrow="Ready to fire"
        title="Templates"
        sub="Design your questions and announcements once — banner, wording, button text and what each answer leads to. In class they're one tap away."
      />

      <div className="flex items-center gap-3 flex-wrap" data-testid="template-kind-switch">
        {[
          { id: "broadcast", label: "Announcements", icon: Megaphone },
          { id: "question", label: "Questions", icon: MessageCircleQuestion },
        ].map((v) => (
          <button
            key={v.id}
            onClick={() => {
              setKind(v.id);
              setDraft(null);
              setEditingId(null);
            }}
            data-testid={`kind-${v.id}`}
            className={`h-10 px-5 rounded-full text-[11px] uppercase tracking-[0.16em] transition-colors flex items-center gap-2 ${
              kind === v.id
                ? "bg-white/[0.08] text-white ring-1 ring-emerald-400/40"
                : "text-white/35 hover:text-white/70 ring-1 ring-white/[0.06]"
            }`}
          >
            <v.icon className="w-3.5 h-3.5" /> {v.label}
          </button>
        ))}
        <button
          onClick={() => {
            setEditingId(null);
            setDraft(kind === "question" ? { ...blankQuestion } : { ...blankBroadcast });
          }}
          className="ml-auto h-10 px-5 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black text-[11px] uppercase tracking-[0.16em] font-semibold"
          data-testid="template-new"
        >
          <Plus className="w-3.5 h-3.5 inline mr-1.5" />
          New {kind === "question" ? "question" : "announcement"}
        </button>
      </div>

      {draft && (
        <Editor
          draft={draft}
          setDraft={setDraft}
          onSave={save}
          onCancel={() => {
            setDraft(null);
            setEditingId(null);
          }}
          saving={saving}
        />
      )}

      <Card testid="templates-list">
        <Eyebrow>{kind === "question" ? "Saved questions" : "Saved announcements"}</Eyebrow>
        {items === null ? (
          <div className="py-8 grid place-items-center text-white/30">
            <Loader2 className="w-4 h-4 animate-spin" />
          </div>
        ) : list.length === 0 ? (
          <Empty
            icon={kind === "question" ? MessageCircleQuestion : Megaphone}
            text="Nothing saved yet — build one above and it'll be waiting for you in class."
            testid="templates-empty"
          />
        ) : (
          <div className="mt-4 space-y-2.5">
            {list.map((t, i) => (
              <div
                key={t.id}
                className="flex items-center gap-3 rounded-2xl bg-black/35 ring-1 ring-white/[0.06] p-3"
                data-testid={`template-row-${t.id}`}
              >
                {t.banner_url ? (
                  <img src={t.banner_url} alt="" className="w-16 h-12 rounded-xl object-cover ring-1 ring-white/10 shrink-0" />
                ) : (
                  <div className="w-16 h-12 rounded-xl bg-white/[0.04] grid place-items-center shrink-0 text-white/25">
                    {t.kind === "question" ? (
                      <MessageCircleQuestion className="w-4 h-4" />
                    ) : (
                      <Megaphone className="w-4 h-4" />
                    )}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] text-white/85 truncate">{t.title}</div>
                  <div className="text-[10.5px] text-white/35 truncate">
                    {t.kind === "question"
                      ? `${t.options?.length || 0} answers · ${t.duration_sec}s`
                      : `${t.button_label || "no button"} · ${t.duration_sec}s`}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => move(i, -1)}
                    className="h-8 w-8 grid place-items-center rounded-full text-white/30 hover:text-white hover:bg-white/[0.06]"
                    data-testid={`template-up-${t.id}`}
                    aria-label="Move up"
                  >
                    <ChevronUp className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => move(i, 1)}
                    className="h-8 w-8 grid place-items-center rounded-full text-white/30 hover:text-white hover:bg-white/[0.06]"
                    data-testid={`template-down-${t.id}`}
                    aria-label="Move down"
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => {
                      setEditingId(t.id);
                      setDraft({
                        kind: t.kind,
                        title: t.title || "",
                        text: t.text || "",
                        link: t.link || "",
                        button_label: t.button_label || "",
                        banner_url: t.banner_url || "",
                        duration_sec: t.duration_sec || 25,
                        options: (t.options || []).map((o) => ({
                          key: o.key,
                          label: o.label || "",
                          follow_up_text: o.follow_up_text || "",
                          follow_up_link: o.follow_up_link || "",
                          follow_up_button: o.follow_up_button || "",
                        })),
                      });
                    }}
                    className="h-8 w-8 grid place-items-center rounded-full text-white/30 hover:text-emerald-300 hover:bg-emerald-500/10"
                    data-testid={`template-edit-${t.id}`}
                    aria-label="Edit"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => remove(t.id)}
                    className="h-8 w-8 grid place-items-center rounded-full text-white/30 hover:text-red-300 hover:bg-red-500/10"
                    data-testid={`template-delete-${t.id}`}
                    aria-label="Delete"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
